import type { Client } from 'filestack-js';
import { EFitOptions } from 'filestack-js';
import type { IObservableArray } from 'mobx';
import { runInAction, toJS } from 'mobx';

import type { Account, Campaign, Creatives, DisplayCreative } from '@feathr/blackbox';
import { AdTagCreative, CreativeClass, EIntegrationTypes } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { DEFAULT_AD_TAG_HTML } from '@feathr/extender/components/Creatives/Creatives.utils';

export { DEFAULT_AD_TAG_HTML };

/**
 * Find all valid dimensions that match an aspect ratio and are smaller than the original
 * @param width - The width of the creative
 * @param height - The height of the creative
 * @param validDimensions - The valid dimensions to find
 * @returns The valid dimensions that match the aspect ratio and are smaller than the original
 */
function findAllValidScalingDimensions(
  width: number,
  height: number,
  validDimensions: Record<string, any>,
): Array<{ spec: string; width: number; height: number }> {
  // Calculate aspect ratio with some tolerance for rounding errors
  const aspectRatio = width / height;
  const results: Array<{ spec: string; width: number; height: number }> = [];

  // Check each valid dimension
  Object.entries(validDimensions).forEach(([spec, dimension]) => {
    const dimensionWidth = dimension.width;
    const dimensionHeight = dimension.height;
    const dimensionRatio = dimensionWidth / dimensionHeight;

    // Check if aspect ratio matches (with small tolerance) and dimensions are smaller
    const ratioMatches = Math.abs(aspectRatio - dimensionRatio) < 0.01;

    // Include dimensions that are smaller or exactly match the original dimensions
    // For smaller dimensions, require at least 1px difference
    const hasSignificantDifference = width - dimensionWidth >= 1 || height - dimensionHeight >= 1;
    const exactMatch = dimensionWidth === width && dimensionHeight === height;
    const isSmallerOrExactMatch =
      (dimensionWidth <= width && dimensionHeight <= height && hasSignificantDifference) ||
      exactMatch;

    if (ratioMatches && isSmallerOrExactMatch) {
      results.push({
        spec: validDimensions[spec].spec,
        width: dimensionWidth,
        height: dimensionHeight,
      });
    }
  });

  return results;
}

/**
 * Check if creative dimensions match any valid aspect ratio
 * @param width - The width of the creative
 * @param height - The height of the creative
 * @param targetWidth - The target width of the creative
 * @param targetHeight - The target height of the creative
 * @returns True if the creative dimensions match any valid aspect ratio, false otherwise
 */
function matchesAspectRatio(
  width: number,
  height: number,
  targetWidth: number,
  targetHeight: number,
): boolean {
  const aspectRatio = width / height;
  const targetAspectRatio = targetWidth / targetHeight;

  // Exact aspect ratio match and minimum dimensions check
  return aspectRatio === targetAspectRatio && width >= targetWidth && height >= targetHeight;
}

/**
 * Check if creative dimensions match any valid aspect ratio
 * @param creative - The creative to check
 * @param validDimensions - The valid dimensions to check
 * @returns True if the creative dimensions match any valid aspect ratio, false otherwise
 */
export function hasValidAspectRatio(
  creative: DisplayCreative,
  validDimensions: Record<string, any>,
): boolean {
  const width = creative.get('width');
  const height = creative.get('height');

  return Object.values(validDimensions).some((dimension) =>
    matchesAspectRatio(width, height, dimension.width, dimension.height),
  );
}

/**
 * Find closest valid dimension by aspect ratio
 * @param creative - The creative to find a closest aspect ratio for
 * @param validDimensions - The valid dimensions to find a closest aspect ratio for
 * @returns The closest aspect ratio
 */
function findClosestAspectRatio(
  creative: DisplayCreative,
  validDimensions: Record<string, any>,
): string {
  const width = creative.get('width');
  const height = creative.get('height');
  const creativeAspectRatio = width / height;

  let closestDimension = '';
  let smallestDiff = Infinity;

  Object.values(validDimensions).forEach((dimension) => {
    const dimensionAspectRatio = dimension.width / dimension.height;
    const diff = Math.abs(creativeAspectRatio - dimensionAspectRatio);

    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestDimension = `${dimension.width}x${dimension.height}`;
    }
  });

  if (!closestDimension) {
    // If no close match, return the smallest valid dimension
    const sortedDimensions = Object.values(validDimensions).sort(
      (a, b) => a.width * a.height - b.width * b.height,
    );
    if (sortedDimensions.length > 0) {
      const smallest = sortedDimensions[0];
      closestDimension = `${smallest.width}x${smallest.height}`;
    }
  }

  return closestDimension;
}

/**
 * Find a spec that matches the aspect ratio exactly and meets minimum dimensions
 * @param creative - The creative to find a spec for
 * @param validDimensions - The valid dimensions to find a spec for
 * @returns The spec that matches the aspect ratio exactly and meets minimum dimensions
 */
function getSpecForAspectRatio(
  creative: DisplayCreative,
  validDimensions: Record<string, any>,
): string | null {
  const width = creative.get('width');
  const height = creative.get('height');
  const creativeAspectRatio = width / height;

  // Find all dimensions with matching aspect ratio and sufficient size
  const validSpecs = Object.values(validDimensions).filter((dimension) => {
    const dimensionAspectRatio = dimension.width / dimension.height;
    return (
      dimensionAspectRatio === creativeAspectRatio &&
      width >= dimension.width &&
      height >= dimension.height
    );
  });

  // If we found valid specs, return the one with the largest dimensions
  if (validSpecs.length > 0) {
    // Sort by total pixels (area) descending
    const sortedBySize = validSpecs.sort((a, b) => b.width * b.height - a.width * a.height);
    return sortedBySize[0].spec;
  }

  return null;
}

/**
 * Get the dimensions of a video
 * @param url - The url of the video
 * @returns The dimensions of the video
 */
export async function getVideoDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise<{ width: number; height: number }>((resolve) => {
    const video = document.createElement('video');
    const handleLoadedMetadata = (): void => {
      const dimensions = {
        width: video.videoWidth,
        height: video.videoHeight,
      };
      // Clean up
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.src = '';
      // Forces the media element to reset
      video.load();
      resolve(dimensions);
    };
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.src = url;
  });
}

/**
 * Process videos, creating a single version for the original dimensions
 * Note: Unlike processImageCreative, this function doesn't use the handle, client,
 * or validDimensionsForScaling as videos are not being resized through Filestack
 * @param width - The width of the video
 * @param height - The height of the video
 * @param mimetype - The mimetype of the video
 * @param filename - The filename of the video
 * @param url - The url of the video
 * @param campaign - The campaign model
 * @param account - The account model
 * @param creatives - The creatives models
 * @param Creatives - The Creatives store
 */
export async function processVideoCreative({
  width,
  height,
  mimetype,
  filename,
  url,
  campaign,
  account,
  creatives,
  Creatives,
}: {
  width: number;
  height: number;
  mimetype: string;
  filename: string;
  url: string;
  campaign: Campaign;
  account: Account;
  creatives: IObservableArray<DisplayCreative>;
  Creatives: Creatives;
}): Promise<void> {
  // Skip resizing for videos - use original dimensions
  const scaledUrl = url;
  const finalWidth = width;
  const finalHeight = height;
  let finalSpec = '';

  const creative = Creatives.create({
    width: finalWidth,
    height: finalHeight,
    mimetype,
    url: scaledUrl,
    _cls: CreativeClass.DisplayVideo,
    parent: campaign.id,
    name: filename,
    is_enabled: true,
    account: account.id,
  }) as DisplayCreative;

  // Try to find a matching spec based on the original dimensions
  const dimensionDefinition = creative.getSpec();
  if (dimensionDefinition) {
    creative.set({ spec: dimensionDefinition.spec });
    finalSpec = dimensionDefinition.spec;
  }

  const creativeValidDimensions = creative.getValidDimensions(campaign);
  const specs = Object.values(creativeValidDimensions).map((v) => v.spec);
  let creativeIsValidDimensions = specs.includes(creative.get('spec'));

  // If we have a valid dimension, add the creative
  if (finalSpec && creativeIsValidDimensions) {
    const response = (await Creatives.add(creative)) as DisplayCreative;
    runInAction(() => {
      creatives.push(response);
    });
    toast(`"${filename}" (${finalWidth}x${finalHeight}) was successfully added`, {
      type: 'success',
    });
    return;
  }

  // For videos, perform normal validation
  const creativeHasValidAspectRatio = hasValidAspectRatio(creative, creativeValidDimensions);

  // File type check
  const lowerCasedFileName = filename.toLowerCase();
  const creativeIsM4v = lowerCasedFileName.endsWith('.m4v');

  if (!creativeIsValidDimensions && creativeHasValidAspectRatio) {
    // Has valid aspect ratio but not exact dimensions
    const matchingSpec = getSpecForAspectRatio(creative, creativeValidDimensions);
    if (matchingSpec) {
      creative.set({ spec: matchingSpec });
      creativeIsValidDimensions = true;
    }
  }

  if (!creativeIsValidDimensions && !creativeHasValidAspectRatio) {
    const closest = findClosestAspectRatio(creative, creativeValidDimensions);
    toast(
      `"${filename}" (${finalWidth}x${finalHeight}) does not match any standard ad size. The recommended size would be ${closest}. Videos must have exact aspect ratios and meet minimum dimensions.`,
      { type: 'error' },
    );
  } else if (creativeIsM4v) {
    toast(
      'Sorry, .m4v files are not supported. Supported filetypes are .jpeg, .png, .gif, .mp4, or .webm',
      { type: 'error' },
    );
  } else {
    const response = (await Creatives.add(creative)) as DisplayCreative;
    runInAction(() => {
      creatives.push(response);
    });
    toast(`"${filename}" (${finalWidth}x${finalHeight}) was successfully added`, {
      type: 'success',
    });
  }
}

/**
 * Create a transformed image using Filestack transformations, then download and store in S3
 * This approach uses Filestack's high-quality transformations but gives us S3 URLs
 */
async function createTransformedImage(
  client: Client,
  handle: string,
  dimension: { width: number; height: number; spec: string },
): Promise<string> {
  // Use Filestack to create the transformation
  const fileStackUrl = client.transform(handle, {
    // Deliberately added to improve quality in #5612
    compress: false,
    quality: { value: 100 },
    resize: {
      fit: EFitOptions.crop,
      height: dimension.height,
      width: dimension.width,
    },
  });

  // Download the transformed image from Filestack
  const response = await fetch(fileStackUrl);
  const transformedBlob = await response.blob();

  // Create a file from the transformed blob
  const transformedFile = new File(
    [transformedBlob],
    `transformed_${dimension.width}x${dimension.height}.jpg`,
    {
      type: 'image/jpeg',
    },
  );

  // Upload the transformed image to S3
  const uploadResult = await client.upload(transformedFile);

  // Construct the S3 URL
  const s3Url = `https://s3.amazonaws.com/${uploadResult.container}/${encodeURIComponent(uploadResult.key)}`;
  return s3Url;
}

/**
 * Process images, creating multiple versions for all matching aspect ratios
 * @param handle - The handle of the image
 * @param client - The Filestack client
 * @param width - The width of the image
 * @param height - The height of the image
 * @param mimetype - The mimetype of the image
 * @param filename - The filename of the image
 * @param url - The url of the image
 * @param campaign - The campaign model
 * @param account - The account model
 * @param validDimensionsForScaling - The valid dimensions for scaling
 * @param creatives - The creatives models
 * @param Creatives - The Creatives store
 */
export async function processImageCreative({
  handle,
  client,
  width,
  height,
  mimetype,
  filename,
  url,
  campaign,
  account,
  validDimensionsForScaling,
  creatives,
  Creatives,
}: {
  handle: string;
  client: Client;
  width: number;
  height: number;
  mimetype: string;
  filename: string;
  url: string;
  campaign: Campaign;
  account: Account;
  validDimensionsForScaling: Record<string, any>;
  creatives: IObservableArray<DisplayCreative>;
  Creatives: Creatives;
}): Promise<void> {
  // Find all valid dimensions that match this image's aspect ratio and are smaller
  const matchingDimensions = findAllValidScalingDimensions(
    width,
    height,
    validDimensionsForScaling,
  );

  // Check if the exact dimensions are found in the matching dimensions because if so,
  // we can skip the transformation process below to save on storage/bandwidth costs
  const exactMatch = matchingDimensions.find((dim) => dim.width === width && dim.height === height);

  // If we found more than one matching dimension, create transformed versions
  if (matchingDimensions.length > 1 || (matchingDimensions.length === 1 && !exactMatch)) {
    // Sort dimensions from largest to smallest to prioritize higher quality versions
    matchingDimensions.sort((a, b) => b.width * b.height - a.width * a.height);

    // Create a creative for each valid dimension
    for (const dimension of matchingDimensions) {
      try {
        // Use the picker with transformations to get S3 URLs directly
        // This approach will store the transformed images in S3 and give us S3 URLs
        const transformedUrl = await createTransformedImage(client, handle, dimension);

        const creative = Creatives.create({
          width: dimension.width,
          height: dimension.height,
          mimetype,
          url: transformedUrl,
          _cls: CreativeClass.DisplayImage,
          parent: campaign.id,
          name: `${filename} (${dimension.width}x${dimension.height})`,
          is_enabled: true,
          account: account.id,
          spec: dimension.spec,
        }) as DisplayCreative;

        const response = (await Creatives.add(creative)) as DisplayCreative;
        runInAction(() => {
          creatives.push(response);
        });
      } catch (error) {
        // Failed to create this version
      }
    }

    // Notify the user that multiple versions were created
    if (matchingDimensions.length > 1) {
      toast(
        `Created ${matchingDimensions.length} versions of "${filename}" for all matching ad sizes`,
        { type: 'success' },
      );
    } else {
      toast(
        `"${filename}" (${width}x${height}) was automatically resized to ${matchingDimensions[0].width}x${matchingDimensions[0].height}`,
        { type: 'success' },
      );
    }

    return;
  }

  // If no matching dimensions were found, skip transformation and use original picker behavior
  // This means the original image (stored in S3 in the original setCreatives function) will be used as-is
  const creativeValidDimensions = validDimensionsForScaling;
  const creative = Creatives.create({
    width,
    height,
    mimetype,
    url,
    _cls: CreativeClass.DisplayImage,
    parent: campaign.id,
    name: filename,
    is_enabled: true,
    account: account.id,
  }) as DisplayCreative;

  const creativeHasValidAspectRatio = hasValidAspectRatio(creative, creativeValidDimensions);

  // If the image isn't an exact dimension but has a valid aspect ratio
  const specs = Object.values(creativeValidDimensions).map((v) => v.spec);
  let creativeIsValidDimensions = specs.includes(creative.get('spec'));

  if (!creativeIsValidDimensions && creativeHasValidAspectRatio) {
    const matchingSpec = getSpecForAspectRatio(creative, creativeValidDimensions);
    if (matchingSpec) {
      creative.set({ spec: matchingSpec });
      creativeIsValidDimensions = true;
    }
  }

  if (!creativeIsValidDimensions && !creativeHasValidAspectRatio) {
    const closest = findClosestAspectRatio(creative, creativeValidDimensions);
    toast(
      `"${filename}" (${width}x${height}) does not match any standard ad size. The closest size would be ${closest}. Images must have exact aspect ratios and meet minimum dimensions.`,
      { type: 'error' },
    );
  } else if (!creativeIsValidDimensions) {
    toast(
      `"${filename}" (${width}x${height}) does not match any standard ad size. Images must match a valid aspect ratio.`,
      { type: 'error' },
    );
  } else {
    // Image is valid - add it to the creatives collection
    const response = (await Creatives.add(creative)) as DisplayCreative;
    runInAction(() => {
      creatives.push(response);
    });
    // Add success toast for images that don't need resizing
    toast(`"${filename}" (${width}x${height}) was successfully added`, { type: 'success' });
  }
}

/**
 * Validate the ad tag creative
 * @param creative - The creative to validate
 * @returns An array of errors
 */
export function validateAdTagCreative(creative: AdTagCreative): string[] {
  const errors: string[] = [];
  const adtagHTML = creative.get('adtag');
  if (!adtagHTML) {
    errors.push('Ad tag HTML cannot be empty.');
    return errors;
  }
  if (!adtagHTML.includes('@FEATHR_TRACK@')) {
    errors.push('Ad tag HTML must include the @FEATHR_TRACK@ impression tracking macro.');
  }
  if (!(adtagHTML.includes('@FEATHR_CLICK@') || adtagHTML.includes('@FEATHR_CLICK_ESC@'))) {
    errors.push(
      'Ad tag HTML must includes a click-through tracking macro: @FEATHR_CLICK@ or @FEATHR_CLICK_ESC@.',
    );
  }
  if (adtagHTML === DEFAULT_AD_TAG_HTML) {
    errors.push('Ad tag HTML must be different than the default HTML');
  }
  return errors;
}

/**
 * Validate the creatives in the step
 * @param campaign - The campaign to validate
 * @param creatives - The creatives to validate
 * @returns An array of errors
 */
export function validateStepCreatives(
  campaign: Campaign,
  creatives: IObservableArray<DisplayCreative>,
): string[] {
  // Use validate directly instead of isValid to avoid computedFn warning when called outside reactive context
  const destinationUrlValidation = campaign.validate(['destination_url'], false);
  if (destinationUrlValidation.errors.length > 0) {
    return destinationUrlValidation.errors;
  }
  if (creatives.filter((t) => t.get('is_enabled')).length < 1) {
    return ['You need at least one enabled creative.'];
  }
  if (creatives.filter((t) => !t.get('is_archived')).length < 1) {
    return ['You need at least one creative.'];
  }
  if (campaign.isOmniCampaign) {
    const creativesWithoutAudiences = creatives.filter(
      (t) =>
        !t.get('is_archived') && (t.get('ad_groups') === null || t.get('ad_groups') === undefined),
    );
    if (creativesWithoutAudiences.length > 0) {
      return ['You need to select at least one audience for each creative.'];
    }

    const activeCreatives = creatives.filter((t) => !t.get('is_archived'));
    const hasMainGroup = activeCreatives.some((t) => {
      const adGroups = t.get('ad_groups');
      // Empty array means targeting both groups
      if (Array.isArray(adGroups) && adGroups.length === 0) {
        return true;
      }
      return adGroups?.includes('main') ?? false;
    });
    const hasLookalikeGroup = activeCreatives.some((t) => {
      const adGroups = t.get('ad_groups');
      // Empty array means targeting both groups
      if (Array.isArray(adGroups) && adGroups.length === 0) {
        return true;
      }
      return adGroups?.includes('lookalike') ?? false;
    });

    const missingGroups: string[] = [];
    if (!hasMainGroup) {
      missingGroups.push('Targeted groups');
    }
    if (!hasLookalikeGroup) {
      missingGroups.push('Lookalike audiences');
    }

    if (missingGroups.length > 0) {
      return [`You need at least one creative assigned to ${missingGroups.join(' and ')}`];
    }
  }

  const integrations = campaign.get('integrations');
  if (integrations?.includes(EIntegrationTypes.Meta)) {
    const hasSquareCreative = creatives.some((c) => {
      const w = c.get('width');
      const h = c.get('height');
      return !c.get('is_archived') && c.get('is_enabled') && w === h && w > 0;
    });
    if (!hasSquareCreative) {
      return ['Meta campaigns require at least one square (1:1) creative.'];
    }
  }

  return creatives
    .map((crv) => {
      let errors: string[] = toJS(
        crv.validate(['width', 'height', 'spec', 'destination_url', 'name'], false).errors,
      );
      const validDimensions = crv.getValidDimensions(campaign);
      const specs = Object.values(validDimensions).map((v) => v.spec);
      const creativeIsValidDimensions = specs.includes(crv.get('spec'));
      const creativeHasValidAspectRatio = hasValidAspectRatio(crv, validDimensions);

      if (!creativeIsValidDimensions && !creativeHasValidAspectRatio) {
        errors.push(
          `${crv.get('width')}x${crv.get('height')} does not match any standard ad size. Images must have exact aspect ratios and meet minimum dimensions.`,
        );
      }
      if (
        crv instanceof AdTagCreative &&
        (crv as AdTagCreative).get('_cls') === CreativeClass.DisplayAdTag
      ) {
        errors = errors.concat(validateAdTagCreative(crv as AdTagCreative));
      }

      if (errors.length) {
        return errors[0];
      }
      return undefined;
    })
    .filter((msg) => !!msg) as string[];
}
